/* eslint-disable @typescript-eslint/no-explicit-any */
import { afterNextRender, Component, Inject, signal } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import linkifyHtml from 'linkify-html';
import * as linkify from 'linkifyjs';
import { DateTime } from 'luxon';
import { catchError, debounceTime, EMPTY, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { MAX_FILE_SIZE_LIMIT } from '../../../../constants/general.constant';
import { getPostTypeLabel, PostTypeEnum } from '../../../../constants/post.constant';
import { getPrivacyTypeLabel, PrivacyTypeEnum } from '../../../../constants/privacy.constant';
import { IBibleVerseModel } from '../../../../models/bible.model';
import { IURLPreviewModel } from '../../../../models/general.model';
import { ICreatePostModel, IPostCategoryModel, IPostModel, ISelectHashtagModel } from '../../../../models/post.model';
import { IUploadPhotoAPIResponse } from '../../../../models/upload.model';
import { GeneralService } from '../../../../services/api/general/general.service';
import { PostService } from '../../../../services/api/post/post.service';
import { UploadService } from '../../../../services/api/upload/upload.service';
import { CreatePostEventService } from '../../../../services/helper/create-post-event/create-post-event.service';
import { LoaderService } from '../../../../services/loader/loader.service';
import { IBasePopupModel } from '../../models/popup.model';
import { BasePopupComponent } from '../base-popup/base-popup.component';
import { BibleVersePopupComponent } from '../bible-verse-popup/bible-verse-popup.component';
import { SuccessPopupComponent } from '../success-popup/success-popup.component';

@Component({
    selector: 'app-create-post-popup',
    standalone: false,
    templateUrl: './create-post-popup.component.html',
    styleUrl: './create-post-popup.component.scss',
})
export class CreatePostPopupComponent extends BasePopupComponent {
    protected PostTypeEnum = PostTypeEnum;
    protected PrivacyTypeEnum = PrivacyTypeEnum;
    protected getPostTypeLabel = getPostTypeLabel;
    protected getPrivacyTypeLabel = getPrivacyTypeLabel;
    protected hashtagsMap = signal<Map<number, ISelectHashtagModel>>(new Map());
    protected appliedTags = signal<string[]>([]);
    protected postImage?: File;
    protected postImageUrl?: string;
    protected urlPreview = signal<IURLPreviewModel | null>(null);
    protected selectedBibleVerses = signal<IBibleVerseModel[]>([]);
    protected coloredQuoteContent!: SafeHtml;
    protected postForm = this.fb.group(
        {
            content: ['', this.data?.isGroup ? [] : [Validators.required]],
            type: [this.data?.isGroup ? PostTypeEnum.Group : PostTypeEnum.Prayer, Validators.required],
            privacy: [PrivacyTypeEnum.Public, Validators.required],
            cats: [[] as string[]],
            photo: [null as number | null],
            photoSelected: [null as File | null], // temporary for validator
            verses: [[] as number[]],
            url: [''],
        },
        {
            validators: (form: AbstractControl) => {
                if (!this.data?.isGroup) {
                    return null;
                }
                const { content, photoSelected } = form.value;
                return content || photoSelected ? null : { atLeastOneRequired: true };
            },
        },
    );
    protected postTypeIconConfig: Partial<Record<PostTypeEnum, { icon: string }>> = {
        [PostTypeEnum.Prayer]: {
            icon: '/assets/images/post/prayer-icon.svg',
        },
        [PostTypeEnum.Post]: {
            icon: '/assets/images/post/encouragement-icon.svg',
        },
        [PostTypeEnum.Testimony]: {
            icon: '/assets/images/post/testimony-icon.svg',
        },
        [PostTypeEnum.Group]: {
            icon: '/assets/images/shared/group-icon.svg',
        },
        [PostTypeEnum.Announcement]: {
            icon: '/assets/images/post/admin-icon.svg',
        },
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) protected override data: any,
        protected override dialogRef: MatDialogRef<CreatePostPopupComponent>,
        private fb: NonNullableFormBuilder,
        private loaderService: LoaderService,
        private postService: PostService,
        private uploadService: UploadService,
        private createPostEventService: CreatePostEventService,
        private dialog: MatDialog,
        private generalService: GeneralService,
        private sanitizer: DomSanitizer,
    ) {
        super(data, dialogRef);
        afterNextRender(() => {
            this.setupLinkExtraction();
            if (data?.isEdit && data?.post) {
                this.loadHashtags().subscribe({
                    next: () => {
                        this.patchPostData(data.post);
                    },
                });
            } else {
                // Just load hashtags normally
                this.loadHashtags().subscribe();
            }
        });
    }

    protected get selectedType(): PostTypeEnum {
        return this.postForm.controls.type.value;
    }

    protected get selectedPrivacy(): PrivacyTypeEnum {
        return this.postForm.controls.privacy.value;
    }

    static override getDialogConfig(): MatDialogConfig<IBasePopupModel> {
        return {
            ...super.getDialogConfig(),
        };
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.coloredQuoteContent = this.highlightHashtagsAndLinks(this.data?.quote?.content);
    }

    protected timeAgo(time: string): string | null {
        return DateTime.fromISO(time).toRelative();
    }

    protected getSelectedTags(): { id: number; name: string }[] {
        return Array.from(this.hashtagsMap().values()).filter((tag) => tag.isSelected);
    }

    protected selectPostType(type: PostTypeEnum): void {
        this.postForm.patchValue({ type });
    }

    protected selectPrivacyType(privacy: PrivacyTypeEnum): void {
        this.postForm.patchValue({ privacy });
    }

    protected toggleTag(tagId: number): void {
        const tag = this.hashtagsMap().get(tagId);
        if (tag) {
            tag.isSelected = !tag.isSelected;
        }
    }

    protected applyTags(): void {
        const selected = this.getSelectedTags().map((t) => t.name);
        this.appliedTags.set(selected);
        this.postForm.patchValue({ cats: selected });
    }

    protected removeTag(tagId: number): void {
        const tag = this.hashtagsMap().get(tagId);
        if (tag) {
            tag.isSelected = false;
        }

        const selected = this.getSelectedTags().map((t) => t.name);
        this.appliedTags.set(selected);
        this.postForm.patchValue({ cats: selected });
    }

    protected removeTagByName(name: string): void {
        const tag = Array.from(this.hashtagsMap().values()).find((t) => t.name === name);
        if (tag) {
            this.removeTag(tag.id);
        }
    }

    protected removeBibleVerse(): void {
        this.selectedBibleVerses.set([]);
        this.postForm.patchValue({ verses: [] });
    }

    protected onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) {
            return;
        }
        if (file.size > MAX_FILE_SIZE_LIMIT) {
            const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
            errorPopupConfig.data = {
                title: `Error`,
                message: `File size must not exceed 10 MB.`,
            };
            this.dialog.open(SuccessPopupComponent, errorPopupConfig);
            input.value = '';
            return;
        }
        this.postImage = file;
        this.postImageUrl = URL.createObjectURL(file);

        // Patch the temporary control so validator sees it
        this.postForm.patchValue({ photoSelected: file });
        this.postForm.updateValueAndValidity();
    }

    protected removePostImage(): void {
        this.postImage = undefined;
        this.postImageUrl = '';
        this.postForm.patchValue({ photo: null });
    }

    protected getVerseReference(verses: IBibleVerseModel[]): string {
        if (!verses) {
            return '';
        }

        const first = verses[0];
        const last = verses[verses.length - 1];

        const bookType = first.type;
        const chapter = first.chapter;

        // Handle single verse or a range
        const versePart = verses.length === 1 ? `${first.list}` : `${first.list}-${last.list}`;

        return `${bookType} ${chapter}:${versePart}`;
    }

    protected openBibleVersePopup(): void {
        const popupConfig = BibleVersePopupComponent.getDialogConfig();
        popupConfig.maxHeight = '90vh';
        const dialogRef = this.dialog.open(BibleVersePopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe((result: IBibleVerseModel[]) => {
            if (result) {
                this.selectedBibleVerses.set(result);
                this.postForm.patchValue({ verses: result.map((item) => item.id) });
            }
        });
    }

    protected submit(): void {
        if (this.postForm.invalid) {
            return;
        }

        this.loaderService.show();

        const photoUpload$: Observable<IUploadPhotoAPIResponse | null> =
            // eslint-disable-next-line no-nested-ternary
            this.data?.isEdit || this.data?.isQuote
                ? this.postImage && this.postImageUrl
                    ? this.uploadPostImage()
                    : of(null)
                : of(null); // skip upload for new post

        photoUpload$
            .pipe(
                switchMap(() => {
                    if (this.data?.isEdit && this.data?.post) {
                        return this.updatePost(this.data.post.id);
                    }
                    if (this.data?.isQuote && this.data?.quote) {
                        return this.quotePost(this.data.quote.id);
                    }
                    if (this.data?.isGroup && this.data?.groupId) {
                        return this.createPost(this.data?.groupId);
                    }
                    return this.createPost();
                }),
                tap((result) => {
                    if (result?.type === 'CREATE_POST_OPTIMISTIC') {
                        this.dialogRef.close(result);
                        return;
                    }
                    this.dialogRef.close(true);
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error submitting post:', error);
                    return EMPTY;
                }),
                finalize(() => this.loaderService.hide()),
            )
            .subscribe();
    }

    private loadHashtags(): Observable<IPostCategoryModel[]> {
        this.loaderService.show();
        return this.postService.getPostCategoryListing(15).pipe(
            tap((response) => {
                this.hashtagsMap.update(() => {
                    const newMap = new Map<number, ISelectHashtagModel>();
                    response.forEach((tag) => {
                        newMap.set(tag.id, {
                            id: tag.id,
                            name: tag.name,
                            isSelected: false,
                        });
                    });
                    return newMap;
                });
            }),
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error loading hashtags:', error);
                return EMPTY;
            }),
            finalize(() => {
                this.loaderService.hide();
            }),
        );
    }

    private uploadPostImage(): Observable<IUploadPhotoAPIResponse> {
        const formData = new FormData();
        formData.append('file', this.postImage!);

        return this.uploadService.uploadPhoto(formData).pipe(
            tap((res) => {
                // Patch actual photo ID
                this.postForm.patchValue({ photo: res.id });
                // clear temporary field control
                this.postForm.patchValue({ photoSelected: null });
            }),
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on upload photo', error);
                return EMPTY;
            }),
        );
    }

    private patchPostData(post: IPostModel): void {
        this.postForm.patchValue({
            content: post.content,
            type: post.type,
            privacy: post.privacy,
            cats: post.cats ?? [],
            photo: post.photoId ?? null,
            verses: post.verses?.map((v) => v.id) ?? [],
            url: post.url ?? '',
        });

        // Patch selected hashtags
        post.postCats?.forEach((cat) => {
            const tag = this.hashtagsMap().get(cat.id);
            if (tag) {
                tag.isSelected = true;
            }
        });

        // Patch image preview
        if (post.photo) {
            this.postImageUrl = post.photo;
        }

        // patch bible verses
        if (post.verses?.length) {
            this.selectedBibleVerses.set(post.verses);
        }
    }

    private createPost(groupId?: number): Observable<{ type: 'CREATE_POST_OPTIMISTIC'; payload: any }> {
        const payload = {
            ...this.postForm.value,
            ...(groupId ? { group: groupId, privacy: null } : {}),
            selectedVerses: this.selectedBibleVerses(),
            photoUrl: this.postImageUrl,
            photoFile: this.postImage,
        };
        return of({
            type: 'CREATE_POST_OPTIMISTIC',
            payload,
        });
    }

    private updatePost(postId: number): Observable<IPostModel> {
        return this.postService.editPost(postId, this.postForm.value as ICreatePostModel).pipe(
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on update post', error);
                return EMPTY;
            }),
        );
    }

    private quotePost(postId: number): Observable<IPostModel> {
        return this.postService.quotePost(postId, this.postForm.value as ICreatePostModel).pipe(
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on quote post', error);
                return EMPTY;
            }),
        );
    }

    private setupLinkExtraction(): void {
        this.postForm.controls.content.valueChanges.pipe(debounceTime(400)).subscribe((text: string) => {
            this.extractLinks(text);
        });
    }

    private extractLinks(text: string): void {
        const control = this.postForm.controls.url;

        if (!text) {
            control.setValue('');
            this.urlPreview.set(null);
            return;
        }

        // Use linkify to find URLs
        const links = linkify.find(text).filter((item) => item.type === 'url');
        const firstUrl = links.length > 0 ? links[0].href : '';

        control.setValue(firstUrl);

        if (!firstUrl) {
            this.urlPreview.set(null);
            return;
        }

        this.generalService
            .getURLPreview(firstUrl)
            .pipe(
                tap((res) => {
                    if (res) {
                        this.urlPreview.set(res);
                    } else {
                        this.urlPreview.set(null);
                    }
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error fetching thumbnail preview:', error);
                    this.urlPreview.set(null);
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    private highlightHashtagsAndLinks(text: string): SafeHtml {
        if (!text) {
            return '';
        }

        // Escape unsafe HTML just in case
        let formatted = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');

        // Replace newlines with <br>
        formatted = formatted.replace(/\n/g, '<br>');

        // Highlight hashtags (make them blue)
        formatted = formatted.replace(/(#\w+)/g, `<span style="color: #4F66FF;">$1</span>`);

        // Use linkify to make URLs clickable
        formatted = linkifyHtml(formatted, {
            target: '_blank',
            rel: 'noopener noreferrer',
            className: 'text-[#4F66FF] underline',
        });

        // Mark as safe HTML for Angular
        return this.sanitizer.bypassSecurityTrustHtml(formatted);
    }
}
